import { Shield, Globe, Car, Activity, RefreshCw, Cloud, FileCheck, Plug, Zap, Code, Grid3X3 } from "lucide-react";

type BadgeVariant = "blue" | "teal" | "red" | "green" | "navy" | "purple" | "orange" | "gold" | "indigo";

interface CertificationBadgeProps {
  type: "fedramp" | "soc2" | "iso27001" | "oca" | "v2g" | "openadr" | "ocpi" | "azure" | "ocpp" | "restapi" | "grid";
  size?: "sm" | "md" | "lg";
  className?: string;
}

const badgeConfig = {
  fedramp: {
    icon: Shield,
    label: "FedRAMP",
    sublabel: "Ready",
    variant: "gold" as BadgeVariant,
  },
  soc2: {
    icon: FileCheck,
    label: "SOC 2",
    sublabel: "Type-2",
    variant: "indigo" as BadgeVariant,
  },
  iso27001: {
    icon: Globe,
    label: "ISO",
    sublabel: "27001",
    variant: "teal" as BadgeVariant,
  },
  oca: {
    icon: Plug,
    label: "OCA",
    sublabel: "certified",
    variant: "red" as BadgeVariant,
  },
  v2g: {
    icon: Car,
    label: "V2G",
    sublabel: "",
    variant: "blue" as BadgeVariant,
  },
  openadr: {
    icon: Activity,
    label: "OpenADR",
    sublabel: "",
    variant: "teal" as BadgeVariant,
  },
  ocpi: {
    icon: RefreshCw,
    label: "OCPI",
    sublabel: "",
    variant: "green" as BadgeVariant,
  },
  azure: {
    icon: Cloud,
    label: "Azure",
    sublabel: "Cloud",
    variant: "blue" as BadgeVariant,
  },
  ocpp: {
    icon: Zap,
    label: "OCPP",
    sublabel: "1.6 & 2.0.1",
    variant: "purple" as BadgeVariant,
  },
  restapi: {
    icon: Code,
    label: "REST",
    sublabel: "APIs",
    variant: "orange" as BadgeVariant,
  },
  grid: {
    icon: Grid3X3,
    label: "Grid",
    sublabel: "Integration",
    variant: "teal" as BadgeVariant,
  },
};

const variantStyles = {
  navy: "bg-gradient-to-r from-[#1a365d] to-[#2d4a7c] text-white border-[#4a6fa5]",
  blue: "bg-gradient-to-r from-[#2563eb] to-[#3b82f6] text-white border-[#60a5fa]",
  teal: "bg-gradient-to-r from-[#0d9488] to-[#14b8a6] text-white border-[#2dd4bf]",
  red: "bg-gradient-to-r from-[#dc2626] to-[#ef4444] text-white border-[#f87171]",
  green: "bg-gradient-to-r from-[#16a34a] to-[#22c55e] text-white border-[#4ade80]",
  purple: "bg-gradient-to-r from-[#7c3aed] to-[#8b5cf6] text-white border-[#a78bfa]",
  orange: "bg-gradient-to-r from-[#ea580c] to-[#f97316] text-white border-[#fb923c]",
  gold: "bg-gradient-to-r from-[#b45309] to-[#d97706] text-white border-[#f59e0b]",
  indigo: "bg-gradient-to-r from-[#4338ca] to-[#6366f1] text-white border-[#818cf8]",
};

const sizeStyles = {
  sm: "px-2.5 py-1 text-xs gap-1.5",
  md: "px-3 py-1.5 text-sm gap-2",
  lg: "px-4 py-2 text-base gap-2.5",
};

const iconSizes = {
  sm: "h-3.5 w-3.5",
  md: "h-4 w-4",
  lg: "h-5 w-5",
};

export function CertificationBadge({ type, size = "md", className = "" }: CertificationBadgeProps) {
  const config = badgeConfig[type];
  const Icon = config.icon;
  
  return (
    <div 
      className={`
        inline-flex items-center rounded-full border-2 shadow-lg
        font-semibold transition-transform hover:scale-105
        ${variantStyles[config.variant]}
        ${sizeStyles[size]}
        ${className}
      `}
    >
      <Icon className={iconSizes[size]} />
      <span className="flex items-baseline gap-1">
        <span>{config.label}</span>
        {config.sublabel && (
          <span className="font-normal opacity-90">{config.sublabel}</span>
        )}
      </span>
    </div>
  );
}

export function CertificationBadgeGroup({ 
  badges, 
  size = "md",
  className = "" 
}: { 
  badges: CertificationBadgeProps["type"][]; 
  size?: CertificationBadgeProps["size"];
  className?: string;
}) {
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      {badges.map((badge) => (
        <CertificationBadge key={badge} type={badge} size={size} />
      ))}
    </div>
  );
}

export default CertificationBadge;
